import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CreditCard, CheckCircle2, AlertCircle, Loader2, ArrowLeft, ShieldCheck, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 10; // ~20s before we tell the user to refresh

/**
 * /billing/onboarding — Dealer onboarding fee (£2,500, one-off).
 *
 *  States the page can be in:
 *    1. NOT PAID — show pitch + "Pay £2,500" button
 *    2. RETURNING FROM STRIPE (session_id in URL) — poll for status
 *    3. PAID — green confirmation + receipt link
 *    4. CANCELLED — friendly retry banner
 *
 * Pricing & business logic lives server-side. The frontend never sends an
 * amount — only the package id ("onboarding") and the window origin so the
 * backend can compose dynamic success/cancel URLs.
 */
export default function BillingOnboardingPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");

  const [state, setState] = useState(null); // onboarding-state payload
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollState, setPollState] = useState(null);
  const [pollAttempt, setPollAttempt] = useState(0);

  // Initial state fetch
  useEffect(() => {
    api.get("/billing/onboarding-state")
      .then((r) => setState(r.data))
      .catch(() => toast.error("Couldn't load billing state"))
      .finally(() => setLoading(false));
  }, []);

  // Poll the checkout status if we're returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    let timer;

    const tick = async () => {
      attempts += 1;
      setPollAttempt(attempts);
      try {
        const r = await api.get(`/billing/checkout/status/${sessionId}`);
        setPollState(r.data);
        if (r.data.payment_status === "paid") {
          setPolling(false);
          toast.success("Payment received — welcome aboard!");
          // Re-fetch onboarding-state so the green confirmation card renders
          api.get("/billing/onboarding-state").then((s) => setState(s.data));
          return;
        }
        if (r.data.status === "expired") {
          setPolling(false);
          toast.error("Payment session expired — please start a new checkout");
          return;
        }
        if (attempts < MAX_POLLS) {
          timer = setTimeout(tick, POLL_INTERVAL);
        } else {
          setPolling(false);
        }
      } catch (e) {
        setPolling(false);
        toast.error(e?.response?.data?.detail || "Couldn't check payment status");
      }
    };
    tick();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId]);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout/onboarding", {
        package_id: "onboarding",
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't start checkout";
      toast.error(msg);
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const alreadyPaid = state?.paid;
  const pkg = state?.package || {};
  const polledPaid = pollState?.payment_status === "paid";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="btn-outline" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <div className="text-sm text-muted-foreground hidden sm:block">
            {state?.workshop_name}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* CANCELLED banner */}
        {cancelled && !sessionId && (
          <div className="mb-6 rounded-lg border border-warning/40 bg-warning/10 p-4 flex items-start gap-3" data-testid="onboarding-cancelled-banner">
            <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">Payment cancelled</div>
              <p className="text-sm text-muted-foreground mt-1">No charge was made. You can restart the checkout whenever you're ready.</p>
              <button onClick={() => setParams({})} className="btn-outline text-xs mt-2" data-testid="dismiss-cancelled-btn">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* PAYMENT STATUS POLLING (returning from Stripe) */}
        {sessionId && !polledPaid && polling && (
          <div className="mb-6 rounded-lg border border-primary/40 bg-primary/5 p-6 flex items-start gap-4" data-testid="onboarding-polling-card">
            <Loader2 className="w-6 h-6 text-primary animate-spin shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-bold text-xl">Confirming your payment…</div>
              <p className="text-sm text-muted-foreground mt-1">
                We're checking with Stripe. This usually takes 2-3 seconds. Don't close this tab — attempt {pollAttempt}/{MAX_POLLS}.
              </p>
            </div>
          </div>
        )}

        {/* PAID — green hero */}
        {(alreadyPaid || polledPaid) && (
          <div className="rounded-xl border border-success/40 bg-success/10 p-8 mb-8" data-testid="onboarding-paid-card">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-success/20 grid place-items-center shrink-0">
                <CheckCircle2 className="w-8 h-8 text-success" />
              </div>
              <div className="flex-1">
                <div className="label-overline text-success">Onboarding complete</div>
                <h1 className="font-display font-bold text-3xl tracking-tight mt-1">Welcome aboard.</h1>
                <p className="text-muted-foreground mt-2">
                  Your onboarding has been received and your workshop is now <strong className="text-foreground">active</strong>.
                  All paid add-ons (Condition Photos, Service Plans, branded SMS) are unlocked.
                </p>
                {state?.paid_at && (
                  <p className="text-xs text-muted-foreground mt-3 font-mono-plex">
                    Paid on {new Date(state.paid_at).toLocaleString("en-GB")}
                  </p>
                )}
                <div className="flex gap-2 mt-5">
                  <button onClick={() => nav("/dashboard")} className="btn-primary" data-testid="onboarding-go-to-dashboard-btn">
                    Go to dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NOT YET PAID — TBA placeholder (pricing hidden pre-pilot-review) */}
        {!alreadyPaid && !polledPaid && (
          <>
            <div className="mb-8">
              <div className="label-overline text-primary">Dealer onboarding</div>
              <h1 className="font-display font-bold text-4xl tracking-tight mt-1">Activate your workshop</h1>
              <p className="text-muted-foreground mt-3 max-w-2xl">
                A one-off onboarding fee covers your dealership setup: branding, IONOS email, Twilio SMS sender ID, service-plan + DVSA wiring, and full team-management onboarding.
                Once paid, your workshop moves from <em>Trial</em> to <em>Active</em>.
              </p>
            </div>

            <div className="card-default p-8" data-testid="onboarding-pay-card">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-full bg-primary/10 grid place-items-center shrink-0">
                  <CreditCard className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display font-black text-5xl tracking-tighter text-foreground" data-testid="onboarding-amount">TBA</span>
                    <span className="text-muted-foreground">post-pilot · confirmed on your Order Form</span>
                  </div>
                  <div className="font-semibold mt-2">{pkg.name}</div>
                  <p className="text-sm text-muted-foreground mt-1">{pkg.description}</p>

                  <ul className="mt-5 space-y-2 text-sm">
                    {[
                      "Full workshop branding (logo, colours, sender ID)",
                      "IONOS SMTP + Twilio SMS configured for your domain",
                      "Service-plan API + DVSA MOT API connected",
                      "Team management — invite unlimited advisors & technicians",
                      "Optional add-ons: Condition Photos, Service Plans",
                    ].map((line) => (
                      <li key={line} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 rounded-lg border border-warning/40 bg-warning/[0.08] p-4 flex items-start gap-3" data-testid="onboarding-tba-notice">
                    <ShieldCheck className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <strong className="block">Pricing to be confirmed post-pilot.</strong>
                      <span className="text-muted-foreground">
                        Onboarding activation is currently offline while we finalise pilot pricing with our founding partners.
                        Please contact <a href="mailto:info@servicesellpro.co.uk" className="text-primary hover:underline">info@servicesellpro.co.uk</a> to discuss your workshop&apos;s activation.
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">
                    By proceeding you confirm the <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link> (clauses 3 &amp; 4).
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-border bg-muted/30 p-4 flex items-start gap-3" data-testid="onboarding-vat-receipt-note">
              <Receipt className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground">
                A VAT receipt will be emailed to your registered address by Stripe immediately after payment. For your accountant: the supplier is <strong className="text-foreground">SERVICESELLPRO LIMITED</strong>.
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
